import { PERMISSIONS } from 'auth/permissions';
import { paths } from 'core/routing/paths';

function pathIs(pathname, value) {
  return pathname === value;
}

function pathStarts(pathname, value) {
  return pathname === value || pathname.startsWith(`${value}/`);
}

export const NAV_GROUPS = [
  {
    id: 'overview',
    labelKey: 'nav.groups.overview',
    items: [
      {
        to: paths.overview,
        key: 'nav.overview',
        icon: 'overview',
        permission: PERMISSIONS.view,
        isActive: (pathname) => pathIs(pathname, paths.overview),
      },
    ],
  },
  {
    id: 'assessment',
    labelKey: 'nav.groups.assessment',
    items: [
      {
        to: paths.patterns,
        key: 'nav.patterns',
        icon: 'patterns',
        permission: PERMISSIONS.view,
        isActive: (pathname) => (
          pathIs(pathname, paths.patterns)
          || pathIs(pathname, paths.patternCreate)
          || /^\/patterns\/[^/]+\/edit$/.test(pathname)
        ),
      },
      {
        to: paths.builder,
        key: 'nav.builder',
        icon: 'builder',
        permission: PERMISSIONS.view,
        isActive: (pathname) => pathStarts(pathname, paths.builder),
      },
      {
        to: paths.weights,
        key: 'nav.weights',
        icon: 'weights',
        permission: PERMISSIONS.view,
        isActive: (pathname) => (
          pathIs(pathname, paths.weights)
          || /^\/patterns\/[^/]+\/weights$/.test(pathname)
          || pathStarts(pathname, '/answers')
        ),
      },
    ],
  },
  {
    id: 'results',
    labelKey: 'nav.groups.results',
    items: [
      {
        to: paths.versions,
        key: 'nav.versions',
        icon: 'versions',
        permission: PERMISSIONS.view,
        isActive: (pathname) => pathStarts(pathname, paths.versions),
      },
      {
        to: paths.topThreeProfiles,
        key: 'nav.topThreeProfiles',
        icon: 'results',
        permission: PERMISSIONS.topThreeProfilesView,
        isActive: (pathname) => pathStarts(pathname, paths.topThreeProfiles),
      },
      {
        to: paths.resultContent,
        key: 'nav.resultContent',
        icon: 'content',
        anyOf: [PERMISSIONS.resultContentView, PERMISSIONS.tagsManage],
        isActive: (pathname) => pathStarts(pathname, paths.resultContent),
      },
      {
        to: paths.attempts,
        key: 'nav.attempts',
        icon: 'attempts',
        permission: PERMISSIONS.attemptsView,
        isActive: (pathname) => pathStarts(pathname, paths.attempts),
      },
    ],
  },
  {
    id: 'operations',
    labelKey: 'nav.groups.operations',
    items: [
      {
        to: paths.specialists,
        key: 'nav.specialists',
        icon: 'specialists',
        anyOf: [PERMISSIONS.specialistsView, PERMISSIONS.specialistsManage],
        isActive: (pathname) => pathStarts(pathname, paths.specialists),
      },
      {
        to: paths.consultations,
        key: 'nav.consultations',
        icon: 'consultations',
        anyOf: [PERMISSIONS.consultationsView, PERMISSIONS.consultationsManage],
        isActive: (pathname) => pathStarts(pathname, paths.consultations),
      },
      {
        to: paths.support,
        key: 'nav.support',
        icon: 'support',
        anyOf: [PERMISSIONS.supportView, PERMISSIONS.supportManage],
        isActive: (pathname) => pathStarts(pathname, paths.support),
      },
    ],
  },
  {
    id: 'governance',
    labelKey: 'nav.groups.governance',
    items: [
      {
        to: paths.legalDocuments,
        key: 'nav.legalDocuments',
        icon: 'legal',
        anyOf: [PERMISSIONS.legalDocumentsView, PERMISSIONS.legalDocumentsManage],
        isActive: (pathname) => pathStarts(pathname, paths.legalDocuments),
      },
    ],
  },
  {
    id: 'companyPlans',
    labelKey: 'nav.groups.companyPlans',
    items: [
      {
        to: paths.companyPlans,
        key: 'nav.companyPlans',
        icon: 'plans',
        anyOf: [PERMISSIONS.plansView, PERMISSIONS.plansManage],
        isActive: (pathname) => pathStarts(pathname, paths.companyPlans),
      },
      {
        to: paths.companies,
        key: 'nav.companies',
        icon: 'companies',
        anyOf: [PERMISSIONS.companiesView, PERMISSIONS.companiesManage],
        isActive: (pathname) => pathStarts(pathname, paths.companies),
      },
      {
        to: paths.planPurchases,
        key: 'nav.planPurchases',
        icon: 'purchases',
        anyOf: [PERMISSIONS.purchasesView, PERMISSIONS.purchasesManage],
        isActive: (pathname) => pathStarts(pathname, paths.planPurchases),
      },
      {
        to: paths.companyEntitlements,
        key: 'nav.companyEntitlements',
        icon: 'entitlements',
        anyOf: [PERMISSIONS.entitlementsView, PERMISSIONS.entitlementsManage],
        isActive: (pathname) => pathStarts(pathname, paths.companyEntitlements),
      },
      {
        to: paths.companyCodeBatches,
        key: 'nav.companyCodeBatches',
        icon: 'batches',
        anyOf: [PERMISSIONS.codesView, PERMISSIONS.codesManage],
        isActive: (pathname) => pathStarts(pathname, paths.companyCodeBatches),
      },
      {
        to: paths.companyAccessCodes,
        key: 'nav.companyAccessCodes',
        icon: 'codes',
        anyOf: [PERMISSIONS.codesView, PERMISSIONS.codesManage],
        isActive: (pathname) => pathStarts(pathname, paths.companyAccessCodes),
      },
      {
        to: paths.sponsoredAssessments,
        key: 'nav.sponsoredAssessments',
        icon: 'sponsored',
        permission: PERMISSIONS.sponsoredResultsView,
        isActive: (pathname) => pathStarts(pathname, paths.sponsoredAssessments),
      },
    ],
  },
];

export function itemIsAllowed(item, hasPermission) {
  if (Array.isArray(item.anyOf) && item.anyOf.length > 0) {
    return item.anyOf.some((slug) => hasPermission(slug));
  }
  return hasPermission(item.permission);
}

export function visibleNavGroups(hasPermission) {
  return NAV_GROUPS
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => itemIsAllowed(item, hasPermission)),
    }))
    .filter((group) => group.items.length > 0);
}

export function activeGroupId(groups, pathname) {
  const match = groups.find((group) => group.items.some((item) => item.isActive(pathname)));
  return match?.id || groups[0]?.id || null;
}

export const CRUMB_LABELS = {
  overview: 'nav.overview',
  patterns: 'nav.patterns',
  builder: 'nav.builder',
  questions: 'builder.newQuestion',
  weights: 'nav.weights',
  versions: 'nav.versions',
  'top-three-profiles': 'nav.topThreeProfiles',
  'result-content': 'nav.resultContent',
  attempts: 'nav.attempts',
  specialists: 'nav.specialists',
  consultations: 'nav.consultations',
  support: 'nav.support',
  tickets: 'support.tickets',
  'legal-documents': 'nav.legalDocuments',
  notifications: 'nav.notifications',
  'company-plans': 'nav.companyPlans',
  companies: 'nav.companies',
  'plan-purchases': 'nav.planPurchases',
  'company-entitlements': 'nav.companyEntitlements',
  'company-code-batches': 'nav.companyCodeBatches',
  'company-access-codes': 'nav.companyAccessCodes',
  'sponsored-assessments': 'nav.sponsoredAssessments',
  result: 'companyPlans.sponsoredResult',
  answers: 'answers.detail',
  new: 'actions.create',
  edit: 'actions.edit',
};

export function crumbsFromPath(pathname, t) {
  const parts = pathname.split('/').filter(Boolean);
  return parts.map((part, index) => ({
    label: CRUMB_LABELS[part] ? t(CRUMB_LABELS[part]) : part,
    to: `/${parts.slice(0, index + 1).join('/')}`,
  }));
}
